import { useEffect, useRef } from "react"
import { useAnnotationsStore } from "store/annotations.store"
import AnnotationService from "services/annotations.service"
import { useViewerStore } from "store/viewer.store"
import type { TPageElevationData } from "types/elevationReferences.type"
import type { TAnnotationCategory } from "types/annotations.type"

export const useAnnotations = (relatedObjects: TPageElevationData[] | undefined) => {
	const instance = useViewerStore((state) => state.instance)
	const { visibility, toggleCategoryVisibility, setUnconnectedVisible, setSelectedAnnotation } = useAnnotationsStore()
	const hasRenderedRef = useRef(false)

	// Set up annotation selection callback
	useEffect(() => {
		AnnotationService.onAnnotationSelected = (metadata) => {
			setSelectedAnnotation(metadata)
		}

		return () => {
			AnnotationService.onAnnotationSelected = undefined
		}
	}, [setSelectedAnnotation])

	// Render annotations when relatedObjects are available and document is loaded
	useEffect(() => {
		if (!instance || !relatedObjects || relatedObjects.length === 0) return

		const docViewer = instance.Core.documentViewer

		// Also listen for document load in case it hasn't loaded yet
		const onDocumentLoaded = () => {
			AnnotationService.renderAnnotations(relatedObjects)
			hasRenderedRef.current = true
		}

		docViewer.addEventListener("documentLoaded", onDocumentLoaded)

		return () => {
			docViewer.removeEventListener("documentLoaded", onDocumentLoaded)
		}
	}, [instance, relatedObjects])

	// Sync visibility changes to service
	useEffect(() => {
		if (!hasRenderedRef.current) return

		const categories = Object.keys(visibility) as TAnnotationCategory[]
		for (const category of categories) {
			AnnotationService.setCategoryVisibility(category, visibility[category])
		}
	}, [visibility])

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			AnnotationService.cleanup()
			hasRenderedRef.current = false
		}
	}, [])

	return { visibility, toggleCategoryVisibility, setUnconnectedVisible }
}
